import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, of, switchMap } from 'rxjs';
import { BadResponseWithError } from '../../../shared/models/bad-response-with-error.interface';
import {
    CommunicationChat, CommunicationChatMessagePin,
    CommunicationChatMessages,
} from '../../models/api/responses/chat.response';
import { ChatHubConnectionService } from '../../services/chat-hub-connection.service';
import { CommunicationChatService } from '../../services/communication-chat.service';
import { CommunicationChatActions } from '../actions/actions-type.action';

@Injectable()
export class CommunicationChatEffect {
    private actions$ = inject(Actions);
    private communicationChatService = inject(CommunicationChatService);
    private chatHubConnectionService = inject(ChatHubConnectionService);

    fetchChatsByTargetId$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CommunicationChatActions.fetchChatsByTargetId),
            switchMap((action) =>
                this.communicationChatService
                    .fetchChatsByTargetId(action.payload.targetId)
                    .pipe(
                        map((response: CommunicationChat[]) =>
                            CommunicationChatActions.fetchChatsByTargetIdSuccess(
                                {
                                    payload: response,
                                }
                            )
                        ),
                        catchError((response: BadResponseWithError) =>
                            of(
                                CommunicationChatActions.fetchChatsByTargetIdFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            )
                        )
                    )
            )
        )
    );

    fetchChatInfo$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CommunicationChatActions.fetchChatInfo),
            switchMap((action) =>
                this.communicationChatService
                    .fetchChatInfo(action.payload.chatId)
                    .pipe(
                        map((response: CommunicationChat) =>
                            CommunicationChatActions.fetchChatInfoSuccess({
                                payload: response,
                            })
                        ),
                        catchError((response: BadResponseWithError) =>
                            of(
                                CommunicationChatActions.fetchChatInfoFailure({
                                    payload: response,
                                })
                            )
                        )
                    )
            )
        )
    );

    sendMessage$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CommunicationChatActions.sendMessageToTheChat),
            switchMap((action) =>
                this.communicationChatService
                    .sendMessage(action.payload.chatId, action.payload.message)
                    .pipe(
                        map(() =>
                            CommunicationChatActions.sendMessageToTheChatSuccess()
                        ),
                        catchError((response: BadResponseWithError) =>
                            of(
                                CommunicationChatActions.sendMessageToTheChatFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            )
                        )
                    )
            )
        )
    );

    saveLastMessage$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CommunicationChatActions.signalRSaveLastReadMessage),
            switchMap((action) =>
                this.chatHubConnectionService
                    .sendReadMessage(
                        action.payload.chatId,
                        action.payload.messageId
                    )
                    .pipe(
                        map(() =>
                            CommunicationChatActions.signalRSaveLastReadMessageSuccess()
                        ),
                        catchError((response: BadResponseWithError) =>
                            of(
                                CommunicationChatActions.signalRSaveLastReadMessageFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            )
                        )
                    )
            )
        )
    );

    fetchChatMessages$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CommunicationChatActions.fetchChatMessages),
            switchMap((action) =>
                this.communicationChatService
                    .fetchChatMessages(
                        action.payload.chatId,
                        action.payload.lastMessageId,
                        action.payload.messagesLoadLimit
                    )
                    .pipe(
                        map((response: CommunicationChatMessages) =>
                            CommunicationChatActions.fetchChatMessagesSuccess({
                                payload: {
                                    response,
                                    patchMessages:
                                        action.payload.patchMessages ?? false,
                                },
                            })
                        ),
                        catchError((response: BadResponseWithError) =>
                            of(
                                CommunicationChatActions.fetchChatMessagesFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            )
                        )
                    )
            )
        )
    );
    fetchPinnedChatMessages$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CommunicationChatActions.fetchPinnedChatMessages),
            switchMap((action) =>
                this.communicationChatService
                    .fetchPinnedChatMessages(
                        action.payload.chatId,
                    )
                    .pipe(
                        map((response) =>
                            CommunicationChatActions.fetchPinnedChatMessagesSuccess({
                                payload: {
                                    response: response as unknown as CommunicationChatMessagePin[],
                                },
                            })
                        ),
                        catchError((response: BadResponseWithError) =>
                            of(
                                CommunicationChatActions.fetchChatMessagesFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            )
                        )
                    )
            )
        )
    );

    deleteChatMessages$ = createEffect(() => this.actions$.pipe(
            ofType(CommunicationChatActions.deleteChatMessage),
            switchMap((action) =>{
                const messageIdFromRequest = action.payload.messageId
                return  this.communicationChatService
                    .deleteChatMessages(
                        action.payload.chatId,
                        action.payload.messageId
                    )
                    .pipe(
                        map(() => CommunicationChatActions.deleteChatMessageSuccess({
                                payload: {messageId:messageIdFromRequest},
                            })

                        ),
                        catchError((response: BadResponseWithError) =>
                            of(
                                CommunicationChatActions.deleteChatMessageFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            )
                        )
                    )
            }

            )
        ));
    pinChatMessages$ = createEffect(() => this.actions$.pipe(
        ofType(CommunicationChatActions.pinChatMessage),
        switchMap((action) =>
            this.communicationChatService
                .pinChatMessages(
                    action.payload.chatId,
                    action.payload.pinId
                )
                .pipe(
                    map((messages) => CommunicationChatActions.pinChatMessageSuccess({
                                payload: {pinnedMessages: messages as unknown as CommunicationChatMessagePin[]},
                            })

                    ),
                    catchError((response: BadResponseWithError) =>
                        of(
                            CommunicationChatActions.fetchChatMessagesFailure(
                                {
                                    payload: response,
                                }
                            )
                        )
                    )
                )
        )
    ));
    deltePinChatMessages$ = createEffect(() => this.actions$.pipe(
        ofType(CommunicationChatActions.DeletepinChatMessage),
        switchMap((action) =>
            this.communicationChatService
                .deletePinChatMessages(
                    action.payload.chatId,
                    action.payload.pinId
                )
                .pipe(
                    map((messages) => CommunicationChatActions.pinChatMessageSuccess({
                                payload: {pinnedMessages: messages as unknown as CommunicationChatMessagePin[]},
                            })

                    ),
                    catchError((response: BadResponseWithError) =>
                        of(
                            CommunicationChatActions.fetchChatMessagesFailure(
                                {
                                    payload: response,
                                }
                            )
                        )
                    )
                )
        )
    ));
    updateChatMessages$ = createEffect(() => this.actions$.pipe(
        ofType(CommunicationChatActions.updateChatMessage),
        switchMap((action) =>
            this.communicationChatService
                .updateChatMessages(
                    action.payload.chatId,
                    action.payload.messageId,
                    action.payload.messageText,
                )
                .pipe(
                    map((message: any) => CommunicationChatActions.updateChatMessageSuccess({
                            payload: {response: message},
                        })
                    ),
                    catchError((response: BadResponseWithError) =>
                        of(
                            CommunicationChatActions.updateChatMessageFailure(
                                {
                                    payload: response,
                                }
                            )
                        )
                    )
                )
        )
    ));
    replyChatMessages$ = createEffect(() => this.actions$.pipe(
        ofType(CommunicationChatActions.replyToChatMessage),
        switchMap((action) =>
            this.communicationChatService
                .replyToChatMessages(
                    action.payload.chatId,
                    action.payload.message,
                    action.payload.parentId,
                )
                .pipe(
                    map((message: any) => CommunicationChatActions.replyToChatMessageSuccess({
                            payload: {message},
                        })
                    ),
                    catchError((response: BadResponseWithError) =>
                        of(
                            CommunicationChatActions.replyToChatMessageFailure(
                                {
                                    payload: response,
                                }
                            )
                        )
                    )
                )
        )
    ));
}
